import React from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { getGuiaConfigByGuideType } from '../../../utils/produccionGuiaPaths';

const CHART_COLORS = ['#0F766E', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4'];

const formatQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatPct = (value) =>
  value == null || Number.isNaN(Number(value)) ? '—' : `${Number(value).toFixed(1)}%`;

const formatShortDate = (value) => {
  if (!value) return '—';
  const [y, m, d] = String(value).split('-');
  if (!y || !m || !d) return value;
  return `${d}/${m}`;
};

const statusLabel = (status) => {
  if (status === 'en_produccion') return 'En producción';
  if (status === 'registrada') return 'Registrada';
  return status || '—';
};

function KpiCard({ label, value, sub, accent = 'border-teal-500' }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm p-5 border-l-4 ${accent}`}>
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 truncate">{value}</p>
      {sub ? <p className="text-xs text-gray-500 mt-1">{sub}</p> : null}
    </div>
  );
}

function ChartCard({ title, subtitle, children, empty }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm h-full">
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      {subtitle ? <p className="text-xs text-gray-500 mt-1 mb-4">{subtitle}</p> : <div className="mb-4" />}
      {empty ? (
        <p className="text-sm text-gray-500 py-16 text-center">Sin datos en el período.</p>
      ) : (
        children
      )}
    </div>
  );
}

export default function ProduccionDashboardView({ data }) {
  const kpis = data?.kpis || {};
  const byDay = data?.by_day || [];
  const byStatus = (data?.by_status || []).filter((r) => Number(r.count) > 0);
  const byWarehouse = data?.by_warehouse || [];
  const byGuideType = data?.by_guide_type || [];
  const topRecetas = data?.top_recetas || [];
  const recentGuias = data?.recent_guias || [];

  const dayChart = byDay.map((row) => ({
    ...row,
    label: formatShortDate(row.date),
  }));

  const warehouseChart = [...byWarehouse]
    .slice(0, 8)
    .map((row) => ({
      ...row,
      label: row.name?.length > 18 ? `${row.name.slice(0, 18)}…` : row.name,
    }))
    .reverse();

  const recetasChart = [...topRecetas]
    .map((row) => ({
      ...row,
      label: row.name?.length > 22 ? `${row.name.slice(0, 22)}…` : row.name,
    }))
    .reverse();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <KpiCard
          label="Guías totales"
          value={formatQty(kpis.guias_total)}
          sub={`${formatQty(kpis.guias_en_produccion)} en producción`}
          accent="border-teal-500"
        />
        <KpiCard
          label="Registradas"
          value={formatQty(kpis.guias_registradas)}
          sub="Pendientes de sacar"
          accent="border-amber-500"
        />
        <KpiCard
          label="% despachadas"
          value={formatPct(kpis.pct_despachadas)}
          sub={`${formatQty(kpis.guias_en_produccion)} de ${formatQty(kpis.guias_total)}`}
          accent="border-blue-500"
        />
        <KpiCard
          label="Platos planificados"
          value={formatQty(kpis.platos_planificados)}
          sub="Suma de cantidades en guías"
          accent="border-violet-500"
        />
        <KpiCard
          label="Platos producidos"
          value={formatQty(kpis.platos_producidos)}
          sub="Solo guías en producción"
          accent="border-emerald-500"
        />
        <KpiCard
          label="Tipos en período"
          value={formatQty(byGuideType.length)}
          sub={byGuideType.map((t) => t.label).join(' · ') || 'Sin guías'}
          accent="border-cyan-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Tendencia diaria" subtitle="Guías y platos por fecha" empty={dayChart.length === 0}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dayChart} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                labelFormatter={(_, payload) => payload?.[0]?.payload?.date || _}
                formatter={(v, name) => [formatQty(v), name === 'guias' ? 'Guías' : 'Platos']}
              />
              <Legend />
              <Line type="monotone" dataKey="guias" name="Guías" stroke="#0F766E" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="platos" name="Platos" stroke="#3B82F6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Por estado" subtitle="Distribución de guías" empty={byStatus.length === 0}>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={byStatus}
                dataKey="count"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={95}
                paddingAngle={2}
              >
                {byStatus.map((entry, index) => (
                  <Cell key={entry.status} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => [formatQty(v), 'Guías']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top recetas" subtitle="Mayor volumen en el período" empty={recetasChart.length === 0}>
          <ResponsiveContainer width="100%" height={Math.max(280, recetasChart.length * 36)}>
            <BarChart data={recetasChart} layout="vertical" margin={{ left: 4, right: 16, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatQty(v)} />
              <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v) => [formatQty(v), 'Cantidad']}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.name || _}
              />
              <Bar dataKey="quantity" fill="#0F766E" radius={[0, 4, 4, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Por almacén" subtitle="Guías y volumen" empty={warehouseChart.length === 0}>
          <ResponsiveContainer width="100%" height={Math.max(280, warehouseChart.length * 40)}>
            <BarChart data={warehouseChart} layout="vertical" margin={{ left: 4, right: 16, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v, name) => [formatQty(v), name === 'guias' ? 'Guías' : 'Platos']}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.name || _}
              />
              <Legend />
              <Bar dataKey="guias" name="Guías" fill="#0F766E" radius={[0, 4, 4, 0]} maxBarSize={14} />
              <Bar dataKey="platos" name="Platos" fill="#3B82F6" radius={[0, 4, 4, 0]} maxBarSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {byGuideType.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-800 mb-3">Por tipo de guía</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {byGuideType.map((row) => (
              <div key={row.guide_type} className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-sm font-medium text-gray-800">{row.label}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatQty(row.guias)} guías · {formatQty(row.platos)} platos
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <h3 className="text-base font-semibold text-gray-800 mb-1">Guías recientes</h3>
        <p className="text-xs text-gray-500 mb-4">Últimas guías del período filtrado</p>
        {recentGuias.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center">Sin guías en el período.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b">
                  <th className="py-2 pr-3">Código</th>
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3">Tipo</th>
                  <th className="py-2 pr-3">Almacén</th>
                  <th className="py-2 pr-3">Estado</th>
                  <th className="py-2 pr-3 text-right">Ítems</th>
                  <th className="py-2 pr-3 text-right">Platos</th>
                  <th className="py-2 text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {recentGuias.map((guia) => {
                  const cfg = getGuiaConfigByGuideType(guia.guide_type);
                  const slug = cfg?.slug || 'producto-terminado';
                  return (
                    <tr key={guia.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 pr-3 font-mono text-xs text-gray-800">{guia.code}</td>
                      <td className="py-2 pr-3 text-gray-600">{guia.guide_date || '—'}</td>
                      <td className="py-2 pr-3 text-gray-700">{cfg?.shortTitle || guia.guide_type}</td>
                      <td className="py-2 pr-3 text-gray-600">{guia.warehouse_name || '—'}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium ${
                            guia.production_status === 'en_produccion'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {statusLabel(guia.production_status)}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right text-gray-600">{formatQty(guia.items_count)}</td>
                      <td className="py-2 pr-3 text-right text-gray-800">{formatQty(guia.platos)}</td>
                      <td className="py-2 text-right">
                        <Link
                          to={`/produccion/guias/${slug}/analisis/${guia.id}`}
                          className="text-teal-700 hover:underline font-medium"
                        >
                          Ver
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
